import {useContext, useEffect, useState, useRef, useCallback} from 'react';
import {useNavigate} from "react-router-dom";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import Checkbox from "@mui/material/Checkbox";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUpRightFromSquare, faPenToSquare} from "@fortawesome/free-solid-svg-icons";
import PatientContext from "../../store/patient-context";
import PropTypes from "prop-types";
import "./UserAdminComponent.css"

const limit = 200;  // Max number of patients retrieved by the server

const getCuratedUserList = (getUserList, printErrorMessage) => {
  return getUserList('', 0, limit).then((response) => {
      const user_data = response.data.user_list.map((user) => (
        {
          id: user.user_id,
          username: user.username,
          givenName: user.given_name,
          familyName: user.family_name,
          email: user.email_address,
          date: 'to do',
          status: 'to do',
          isActive: true
        }
      ))

      return user_data;
    }
  ).catch((error) => {
    console.error(error);
    printErrorMessage(error.message);
    return [];
  });
}

function UserAdminComponent(props) {
  const navigate = useNavigate();
  const patientCtx = useContext(PatientContext);
  const [message, setMessage] = useState('');
  const messageTimer = useRef();
  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const {heading, userRole, getUserList, addUser, editUser} = props

  const componentHeading = heading ? heading : userRole.charAt(0).toUpperCase() + userRole.slice(1)

  const printErrorMessage = (message) => {
    const messageDisplayTime = 5000 // ms
    clearTimeout(messageTimer.current)
    setMessage(message);
    messageTimer.current = setTimeout(() => {setMessage("");}, messageDisplayTime);
  }

  const handleUpdateUserList = useCallback( () => {
    if(getUserList) {
      getCuratedUserList(getUserList, printErrorMessage).then((user_list) => {
        setUserList(user_list);
      });
    }
  }, [getUserList]);

  useEffect(() => {
    handleUpdateUserList();
  }, [handleUpdateUserList]);

  const onAddUser = () => {
    if (!addUser) {
      console.debug("Warning: addUser prop not provided.");
      return;
    }
    addUser(handleUpdateUserList);
  };

  const onActivateUsers = (user_array) => {
    if (!editUser) {
      console.debug("Warning: editUser prop not provided.");
      return;
    }
    console.debug("Warning: To do.");
  };
  const onDeactivateUsers = (user_array) => {
    if (!editUser) {
      console.debug("Warning: editUser prop not provided.");
      return;
    }
    console.debug("Warning: To do.");
  };

  const onUserEdit = (event, userInfo) => {
    if (!editUser) {
      console.debug("Warning: editUser prop not provided.");
      return;
    }
    editUser(userInfo, handleUpdateUserList);
  };

  const onUserSelection = (event, id, current_selection) => {
    let new_array = [...current_selection];
    if (! selectedUser.includes(id)) {
      new_array = [...current_selection, id];
    } else {
      new_array = current_selection.filter((x) => x !== id)
    }
    setSelectedUser(new_array);
  };

  const onUserMakeCurrent = (event, userInfo) => {
    patientCtx.update(userInfo.id, `${userInfo.givenName} ${userInfo.familyName}`);
    navigate('/sessions/');
  };

  let columns = [];
  if (userRole !== 'patient') {
    columns.push({ field: 'username', headerName: 'Username', flex: 2});
  }
  columns.push(
    { field: 'givenName', headerName: 'Given Name', flex: 2},
    { field: 'familyName', headerName: 'Family Name', flex: 2},
    { field: 'email', headerName: 'Email Address', flex: 2},
    { field: 'date', headerName: 'Added Date', flex: 1},
    { field: 'status', headerName: 'Status', flex: 1});


  if (userRole === 'patient') {
    columns.push({ field: 'sessions', headerName: "Sessions", type: 'actions', flex: 0,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className={"fa-1x"} />}
          label="See sessions"
          onClick={(event) => onUserMakeCurrent(event, params.row)}
        />
      ]})
  }
  columns.push({ field: 'selected', headerName: 'Select', flex: 0, type: 'actions',
    getActions: (params) => [
      <Checkbox
        color="primary"
        checked={selectedUser.includes(params.row.id)}
        onClick={(event) => onUserSelection(event, params.row.id, selectedUser)}
        inputProps={{ 'aria-label': 'controlled'}}
      />
    ]
  });
  columns.push({ field: 'edit', headerName: "Edit", type: 'actions', flex: 0,
    getActions: (params) => [
      <GridActionsCellItem
        icon={<FontAwesomeIcon icon={faPenToSquare} className={"fa-1x"} />}
        label="Set Active"
        onClick={(event) => onUserEdit(event, params.row)}
      />
    ]});

  // const style = { height: '60vh', width: '100%' };
  const style = { width: '100%' };

  return(
    <section>
      <h2>
        {componentHeading}
      </h2>
      <div style={style}>
        <DataGrid
          id="datagridPatientSessions"
          rows={userList}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 5, page: 0 },
            },
          }}
        />
      </div>
      <h4>{`Actions on ${userRole} users:`}</h4>
      <button id="button-add" className={"button-admin"} onClick={onAddUser}>
        {"Add"}
      </button>
      <button id="button-activate" className={"button-admin"} disabled={selectedUser.length < 1}
            onClick={onActivateUsers}>
        {"Activate selected"}
      </button>
      <button id="button-deactivate" className={"button-admin"} disabled={selectedUser.length < 1}
            onClick={onDeactivateUsers}>
        {"Deactivate selected"}
      </button>
      {message && <div className={"error_message"}>{message}</div>}
      <hr style={{marginTop: "3rem", marginBottom: "5rem"}}/>
    </section>
  );
}

UserAdminComponent.propTypes = {
  heading: PropTypes.string,  // Do not use title. "Title" is a regular attribute used to display tooltip help
  userRole: PropTypes.string.isRequired,
  // using props named "on..." cause React to warn about Unknown Prop ex: onConfirm
  getUserList: PropTypes.func,
  addUser: PropTypes.func,
  editUser: PropTypes.func,
}

export default UserAdminComponent;